<script>
  import { toggleSiri } from '../../os/commands.svelte.js';
  import { portfolio } from '../../os/portfolio.svelte.js';
  import StatusPopovers from './StatusPopovers.svelte';

  let clock = $state(formatClock());
  let openPopover = $state(null); // 'volume' | 'battery' | 'wifi' | 'bluetooth' | null
  let clockExpanded = $state(false);

  // Simulated hardware — creative stand-ins for the real menu extras.
  let batteryLevel = $state(87);

  function formatClock() {
    const d = new Date();
    const day = d.toLocaleDateString([], { weekday: 'short' });
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    return `${day} ${time}`;
  }

  function formatDate() {
    const d = new Date();
    return d.toLocaleDateString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  $effect(() => {
    const id = setInterval(() => (clock = formatClock()), 15000);
    return () => clearInterval(id);
  });

  // The simulated battery slowly "drains" while you browse, then recharges.
  $effect(() => {
    const id = setInterval(() => {
      batteryLevel = batteryLevel <= 20 ? 100 : batteryLevel - 1;
    }, 90000);
    return () => clearInterval(id);
  });

  function toggle(which, e) {
    e?.stopPropagation();
    openPopover = openPopover === which ? null : which;
    clockExpanded = false;
  }

  function toggleClock() {
    clockExpanded = !clockExpanded;
    openPopover = null;
  }

  function closeAll() {
    openPopover = null;
    clockExpanded = false;
  }
</script>

<svelte:window onpointerdown={closeAll} />

<span
  class="status"
  aria-label="Bluetooth"
  onclick={(e) => toggle('bluetooth', e)}
  role="button"
  tabindex="0"
  onkeydown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') toggle('bluetooth', e);
  }}
>
  <svg viewBox="0 0 16 16" role="img">
    <path
      d="M7 2 L10 5 L7 8 L7 2 Z M7 8 L10 11 L7 14 L7 8 Z M7 8 L4 5 M7 8 L4 11"
      fill="none"
      stroke="currentColor"
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
</span>
<span
  class="status"
  aria-label="AirPort"
  onclick={(e) => toggle('wifi', e)}
  role="button"
  tabindex="0"
  onkeydown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') toggle('wifi', e);
  }}
>
  <svg viewBox="0 0 16 16" role="img">
    <circle cx="8" cy="12" r="1.3" fill="currentColor" />
    <path
      d="M4.8 8.7 a4.6 4.6 0 0 1 6.4 0"
      fill="none"
      stroke="currentColor"
      stroke-width="1.3"
      stroke-linecap="round"
    />
    <path
      d="M2.7 6.4 a7.6 7.6 0 0 1 10.6 0"
      fill="none"
      stroke="currentColor"
      stroke-width="1.3"
      stroke-linecap="round"
    />
  </svg>
</span>
<span
  class="status battery"
  aria-label="Battery"
  onclick={(e) => toggle('battery', e)}
  role="button"
  tabindex="0"
  onkeydown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') toggle('battery', e);
  }}
>
  <svg viewBox="0 0 16 16" role="img">
    <rect
      x="1.5"
      y="5"
      width="11"
      height="6"
      rx="1"
      fill="none"
      stroke="currentColor"
      stroke-width="1.2"
    />
    <rect
      x="3"
      y="6"
      width={Math.max(0.5, (batteryLevel / 100) * 8)}
      height="4"
      rx="0.5"
      fill="currentColor"
    />
    <line
      x1="14"
      y1="6.5"
      x2="14"
      y2="9.5"
      stroke="currentColor"
      stroke-width="1.2"
      stroke-linecap="round"
    />
  </svg>
  <small class="battery-pct">{batteryLevel}%</small>
</span>
<span
  class="status"
  aria-label="Volume"
  onclick={(e) => toggle('volume', e)}
  role="button"
  tabindex="0"
  onkeydown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') toggle('volume', e);
  }}
>
  <svg viewBox="0 0 16 16" role="img">
    <path d="M2.5 6 h2.5 l3 -2.5 v9 l-3 -2.5 h-2.5 z" fill="currentColor" />
    {#if (portfolio.settings.alertVolume ?? 0.7) > 0 && portfolio.settings.uiSounds !== false}
      <path
        d="M10 5.5 a3 3 0 0 1 0 5"
        fill="none"
        stroke="currentColor"
        stroke-width="1.2"
        stroke-linecap="round"
      />
      <path
        d="M11.6 3.6 a5.4 5.4 0 0 1 0 8.8"
        fill="none"
        stroke="currentColor"
        stroke-width="1.2"
        stroke-linecap="round"
      />
    {:else}
      <path
        d="M10 6 l4 4 M14 6 l-4 4"
        stroke="currentColor"
        stroke-width="1.2"
        stroke-linecap="round"
      />
    {/if}
  </svg>
</span>
<span
  class="clock"
  aria-label="Date and time"
  onclick={toggleClock}
  role="button"
  tabindex="0"
  onkeydown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') toggleClock();
  }}
>
  {#if clockExpanded}
    {formatDate()}
  {:else}
    {clock}
  {/if}
</span>
<span
  class="spotlight"
  aria-label="Spotlight search"
  onclick={toggleSiri}
  role="button"
  tabindex="0"
  onkeydown={(e) => {
    if (e.key === 'Enter' || e.key === ' ') toggleSiri();
  }}
>
  <svg viewBox="0 0 16 16" role="img">
    <circle cx="6.5" cy="6.5" r="4.4" fill="none" stroke="currentColor" stroke-width="1.5" />
    <line
      x1="9.8"
      y1="9.8"
      x2="14"
      y2="14"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
    />
  </svg>
</span>

<StatusPopovers {openPopover} {batteryLevel} />

<style>
  /* Status chips share the menu bar's bumping 3D language. */
  .status,
  .clock,
  .spotlight {
    display: inline-flex;
    align-items: center;
    border-radius: 3px;
    transform: translateY(0) translateZ(0);
    transition:
      transform 0.16s cubic-bezier(0.34, 1.56, 0.64, 1),
      background 0.12s ease,
      box-shadow 0.12s ease;
  }
  .status {
    gap: 3px;
    padding: 0 5px;
    color: #f1f1f1;
    cursor: pointer;
  }
  .status svg,
  .spotlight svg {
    display: block;
    width: 16px;
    height: 16px;
  }
  .battery-pct {
    font-size: 10px;
    opacity: 0.85;
  }
  .clock {
    font-variant-numeric: tabular-nums;
    padding: 0 8px;
    font-weight: 500;
    cursor: default;
  }
  .spotlight {
    padding: 0 8px 0 4px;
    color: #f1f1f1;
    cursor: pointer;
  }

  /* Bumping hover: pop toward the viewer with a bright top bevel. */
  .status:hover,
  .clock:hover,
  .spotlight:hover {
    background: rgba(255, 255, 255, 0.16);
    transform: translateY(-1px) translateZ(6px) scale(1.05);
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.4),
      0 2px 3px rgba(0, 0, 0, 0.5);
  }
  /* Pressed-in: sink away, invert the bevel. */
  .status:active,
  .clock:active,
  .spotlight:active {
    transform: translateY(1px) translateZ(-2px) scale(0.98);
    background: rgba(0, 0, 0, 0.22);
    box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.5);
  }

  @media (prefers-reduced-motion: reduce) {
    .status,
    .clock,
    .spotlight {
      transition:
        background 0.12s ease,
        box-shadow 0.12s ease;
      transform: none;
    }
    .status:hover,
    .clock:hover,
    .spotlight:hover,
    .status:active,
    .clock:active,
    .spotlight:active {
      transform: none;
    }
  }
</style>
